/**
 * Video Player Component
 * Manages video playback with viewport and hover triggers
 */

import type { Magics } from 'alpinejs';

interface VideoPlayerData {
    isLoaded: boolean;
    isPlaying: boolean;
    trigger: string;
    _video: HTMLVideoElement | null;
    init(this: VideoPlayerContext): void;
    play(this: VideoPlayerContext): void;
    pause(this: VideoPlayerContext): void;
    togglePlayback(this: VideoPlayerContext): void;
}

type VideoPlayerContext = VideoPlayerData & Magics<VideoPlayerData>;

export default function videoPlayer() {
    return {
        // Component state
        isLoaded: false,
        isPlaying: false,
        trigger: 'viewport',
        _video: null,

        // Initialize component
        init() {
            // Read trigger from dataset
            this.trigger = this.$el.dataset.trigger || 'viewport';

            this._video = this.$el.querySelector('video') as HTMLVideoElement | null;
            if (this._video) {
                // Attach canplay listener to set isLoaded
                this._video.addEventListener('canplay', () => {
                    this.isLoaded = true;
                });

                // Attach play/pause listeners to track playing state
                this._video.addEventListener('play', () => {
                    this.isPlaying = true;
                });

                this._video.addEventListener('pause', () => {
                    this.isPlaying = false;
                });

                // Upgrade preload when trigger first fires (play is called)
                const originalPlay = this._video.play.bind(this._video);
                this._video.play = async function (this: HTMLVideoElement) {
                    if (this.preload === 'none') {
                        this.preload = 'auto';
                    }
                    return originalPlay();
                };
            }
        },

        // Play video
        play() {
            if (this._video) {
                this._video.play().catch(() => {
                    // Silently handle autoplay restrictions
                });
            }
        },

        // Pause video
        pause() {
            if (this._video) {
                this._video.pause();
            }
        },

        // Toggle playback
        togglePlayback() {
            this.isPlaying ? this.pause() : this.play();
        },
    } as VideoPlayerData;
}
